import {
  PaywallApi,
  PaywallProps,
  PaywallSession,
  PluginType,
} from '@accedo/one-plugin-sdk';
import { ErrorBoundary } from '@dynamic/ui';
import React, { Suspense, useCallback, useMemo } from 'react';

import { useGlobalContext } from '#/components/useGlobalContext';
import { usePaywall } from '#/hooks';
import { usePaywallConfig } from '#/hooks/usePaywallConfig';
import { fetchMppToken, getPaywallSession } from '#/plugins/paywall/utils';
import { authActions, useAuthentication } from '#/store/auth';

import pluginManager from '../pluginManager';

const onError = (error: Error, stack: string) => {
  console.error('Paywall plugin error', error, stack);
};

const PaywallManager = () => {
  const {
    boot: { isPluginManagerReady },
  } = useGlobalContext();

  const { apiRef, productIds } = usePaywall();
  const paywallConfig = usePaywallConfig();
  const auth = useAuthentication();

  const { sessionEndpoint } = paywallConfig?.properties || {};

  /**
   * Gets session token, but to make sure we are up to date we refresh the profile first.
   * And we analyze profile response to see if there is an entitlement error. If so,
   * we should prevent showing the paywall.
   */
  const getSession = useCallback<
    () => Promise<PaywallSession | null>
  >(async () => {
    const profile = await authActions.refreshProfile();

    if (profile?.errors?.['entitlements']) {
      return Promise.reject('Entitlements error');
    }

    // TODO: Remove fetchMppToken once we added sessionEndpoint to all paywall configs
    return sessionEndpoint
      ? getPaywallSession(sessionEndpoint).then(({ data }) => data)
      : fetchMppToken().catch((err) => {
          console.error('MPP: authentication failed', err);

          return { sessionToken: null, accountReference: null };
        });
  }, [sessionEndpoint]);

  const PaywallComponent = useMemo(() => {
    if (!paywallConfig?.plugin?.id || !isPluginManagerReady) {
      return null;
    }

    return pluginManager.getPluginComponent<PaywallProps>({
      type: PluginType.Paywall,
      id: paywallConfig.plugin.id,
      optional: true,
    });
  }, [paywallConfig, isPluginManagerReady]);

  if (!PaywallComponent) {
    return null;
  }

  return (
    <ErrorBoundary onError={onError}>
      <Suspense fallback={null}>
        <PaywallComponent
          apiRef={apiRef as React.MutableRefObject<PaywallApi>}
          authState={auth}
          getSession={getSession}
          key={`${auth.isLoggedIn}`}
          purchasablePlanIds={productIds}
        />
      </Suspense>
    </ErrorBoundary>
  );
};

PaywallManager.displayName = 'PaywallManager';

export default PaywallManager;
